import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeState {
  mode: ThemeMode;
  isDark: boolean;
}

const getSystemTheme = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

const loadPersistedTheme = (): ThemeState => {
  try {
    const stored = localStorage.getItem('ssp-enterprise-theme');
    if (stored) {
      const parsed = JSON.parse(stored) as { mode: ThemeMode };
      const mode = parsed.mode;
      const isDark = mode === 'system' ? getSystemTheme() : mode === 'dark';
      return { mode, isDark };
    }
  } catch {
    // Ignore errors
  }
  return { mode: 'system', isDark: getSystemTheme() };
};

const initialState: ThemeState = loadPersistedTheme();

const themeSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {
    setMode: (state, action: PayloadAction<ThemeMode>) => {
      state.mode = action.payload;
      state.isDark = action.payload === 'system' ? getSystemTheme() : action.payload === 'dark';
    },
    toggleTheme: (state) => {
      state.mode = state.isDark ? 'light' : 'dark';
      state.isDark = !state.isDark;
    },
    syncSystemTheme: (state) => {
      if (state.mode === 'system') {
        state.isDark = getSystemTheme();
      }
    },
  },
});

export const { setMode, toggleTheme, syncSystemTheme } = themeSlice.actions;
export default themeSlice.reducer;
